import { Bill } from '@enview/interface/types/bills/Bill';
import {
  USER_SELECTABLE_WORKFLOW_STATUSES,
  WorkflowStatus,
} from '@enview/interface/types/triage/WorkflowStatus';
import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { BillTriageAPI, TeamAPI } from '../../../api';
import { getOrgForTeamMode, getTeamMode } from '../../../dux';
import { State } from '../../../dux/@types';
import filteredTeamMembershipsForSelector, {
  getPrimaryWorkspaceId,
} from '../../../helpers/TeamHelper';
import { useToast } from '../Toast/ToastProvider';
import { bulkStatusMessage } from './workflowStatusToastMessages';
import useUndoWorkflowStatus from './useUndoWorkflowStatus';

type BulkWorkflowStatusContentProps = {
  bills: Bill[];
};

// Bulk Workflow Status action: applies one status to every selected bill within a single
// workspace — bulkWorkflowUpdate takes one teamId per call, so there's no per-bill
// workspace to target. Prefers the user's active team mode when every selected bill
// belongs to it (the common case: bulk-selecting rows on a single-workspace page), falling
// back to the first workspace shared by all selected bills, same resolution as the column.
const BulkWorkflowStatusContent = (
  props: BulkWorkflowStatusContentProps,
): ReactElement => {
  const { bills } = props;

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const currentTeamModeId = useSelector(getTeamMode);
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  const userWorkspaces = filteredTeamMembershipsForSelector(
    teamMemberships,
    orgUser,
    orgId,
  );

  const billWorkspaceIdSets = (bills || []).map((bill) =>
    (bill.workspaces ?? [])
      .map((workspace) => workspace.id)
      .filter((id): id is number => id !== undefined),
  );
  const sharedWorkspaceIds = billWorkspaceIdSets.length
    ? billWorkspaceIdSets.reduce((shared, ids) =>
        shared.filter((id) => ids.includes(id)),
      )
    : [];

  const teamId = getPrimaryWorkspaceId(
    userWorkspaces,
    sharedWorkspaceIds,
    currentTeamModeId,
  );

  const [bulkUpdateWorkflowStatus, { isLoading: statusUpdating }] =
    BillTriageAPI.endpoints.bulkUpdateWorkflowStatus.useMutation();
  const { showToast } = useToast();
  const undoWorkflowStatus = useUndoWorkflowStatus();

  const handleSelect = (status: WorkflowStatus): void => {
    if (!orgId || !teamId) return;
    const previousStatuses = bills.map((bill) => ({
      billId: bill.id,
      status:
        (bill.customFields?.[orgId]?.[teamId]?.['Workflow Status']?.fieldValue as
          | WorkflowStatus
          | undefined) ?? null,
    }));
    void bulkUpdateWorkflowStatus({
      orgId,
      teamId,
      billIds: bills.map((bill) => bill.id),
      status,
    }).then((result) => {
      if ('error' in result) return;
      showToast({
        message: bulkStatusMessage(bills.length, status),
        onUndo: () => undoWorkflowStatus({ orgId, teamId, previousStatuses }),
      });
    });
  };

  // Once one status button is clicked, every button (including itself) disables
  // until that bulk write resolves, so a second click can't fire a conflicting
  // status onto the same bills mid-request.
  const disabled = !orgId || !teamId || !bills?.length || statusUpdating;

  return (
    <>
      {USER_SELECTABLE_WORKFLOW_STATUSES.map((status) => (
        <div key={`${status}-bulkdropdown-workflow-status`}>
          <button
            className="dropdown-item"
            disabled={disabled}
            onClick={() => handleSelect(status)}
            type="button"
          >
            <span className="label-text">{status}</span>
          </button>
        </div>
      ))}
    </>
  );
};

export default BulkWorkflowStatusContent;
